/**
 * Ryder Cup Member Stats Filtering and Sorting Utilities
 *
 * Business logic for filtering and sorting Ryder Cup member statistics.
 * Extracted from presentational component to enable testing and reusability.
 */

import type { MemberRyderCupStats } from './ryder-cup-member-stats-container';
import { formatMemberName } from '../utils/members';

export type MemberFilter = 'all' | 'current' | 'active';
export type SortField = 'points' | 'pointsPercentage' | 'appearances' | 'name';
export type SortOrder = 'asc' | 'desc';

/**
 * Filter member stats based on filter criteria
 * @param stats - Array of member stats
 * @param filter - Filter type (all, current team members, active members)
 * @returns Filtered array of member stats
 */
export const filterMemberStats = (stats: MemberRyderCupStats[], filter: MemberFilter): MemberRyderCupStats[] => {
  let filtered = stats;

  // Apply member filter
  if (filter === 'current') {
    filtered = filtered.filter((stat) => stat.teamInfo !== undefined);
  } else if (filter === 'active') {
    filtered = filtered.filter((stat) => stat.isActiveMember);
  }

  // Only show members with at least one point or appearance
  return filtered.filter((stat) => stat.totalAvailablePoints > 0);
};

/**
 * Sort member stats by specified field and order
 * @param stats - Array of member stats
 * @param sortBy - Field to sort by
 * @param sortOrder - Sort direction (asc/desc)
 * @returns Sorted array of member stats
 */
export const sortMemberStats = (
  stats: MemberRyderCupStats[],
  sortBy: SortField,
  sortOrder: SortOrder
): MemberRyderCupStats[] => {
  return [...stats].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'points':
        comparison = a.totalPointsScored - b.totalPointsScored;
        break;
      case 'pointsPercentage':
        comparison = a.pointsPercentage - b.pointsPercentage;
        break;
      case 'appearances':
        comparison = a.usaAppearances + a.europeAppearances - (b.usaAppearances + b.europeAppearances);
        break;
      case 'name':
        comparison = formatMemberName(a.member).localeCompare(formatMemberName(b.member));
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });
};

/**
 * Filter and sort member stats in one operation
 * @param stats - Array of member stats
 * @param filter - Filter type
 * @param sortBy - Field to sort by
 * @param sortOrder - Sort direction
 * @returns Filtered and sorted array of member stats
 */
export const filterAndSortMemberStats = (
  stats: MemberRyderCupStats[],
  filter: MemberFilter,
  sortBy: SortField,
  sortOrder: SortOrder
): MemberRyderCupStats[] => {
  const filtered = filterMemberStats(stats, filter);
  return sortMemberStats(filtered, sortBy, sortOrder);
};
